import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAppContext } from "../../../AppContext";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { isAxiosError } from "../../../common/infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import {
  AiAssistantBanner,
  AiSendOutcome,
  AiStreamEvent,
  IAiConversation,
  IAiConversationListItem,
  IAiMessageImage,
  IAiPendingTurn,
  aiAssistantTexts,
} from "../models/aiAssistantModels";
import {
  formatResetDate,
  normalizeConversation,
  normalizeConversationList,
} from "../utilities/aiAssistantDataUtility";
import { buildChatEntries, findRetryTarget } from "../utilities/aiChatEntriesUtility";
import { sendAiAssistantMessage } from "./aiAssistantStream";
import { useAiAssistantStatus } from "./useAiAssistantStatus";

/** Streamed text is applied at most this often, not on every token. */
const TEXT_FLUSH_MS = 40;

/** After a stop or a dropped connection the server still finishes; look for its answer then. */
const LATE_REFRESH_DELAYS_MS = [3000, 10000];

const CONVERSATION_PARAM = "c";

const isActivePhase = (turn: IAiPendingTurn | null) =>
  !!turn && (turn.phase === "waiting" || turn.phase === "streaming");

/** A 4xx answer to GetConversation: the conversation is gone or not this user's. */
const isClientError = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
};

/**
 * The assistant's conversation state: the list, the open conversation (`?c=<id>` in the URL so a
 * reload keeps it), and the message being sent — optimistic question, streamed answer, tool
 * status, support draft or created ticket — until the stored conversation covers it.
 */
export const useAiAssistant = () => {
  const { selectedStudio, currentUser } = useAppContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedConversationId = searchParams.get(CONVERSATION_PARAM) || null;

  const { status } = useAiAssistantStatus();

  const [turn, setTurnState] = useState<IAiPendingTurn | null>(null);
  const [budgetReachedLocally, setBudgetReachedLocally] = useState(false);
  const [fullConversationIds, setFullConversationIds] = useState<string[]>([]);

  // The turn is mirrored in a ref so stream callbacks and effects always see the latest one,
  // even before React re-renders.
  const turnRef = useRef<IAiPendingTurn | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pendingTextRef = useRef<{ key: string; text: string } | null>(null);
  const textTimerRef = useRef<number | null>(null);
  const lateRefreshTimersRef = useRef<number[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingDeleteIdRef = useRef<string | null>(null);

  const setTurn = useCallback((next: IAiPendingTurn | null) => {
    turnRef.current = next;
    setTurnState(next);
  }, []);

  const updateTurn = useCallback(
    (key: string, update: (current: IAiPendingTurn) => IAiPendingTurn) => {
      const current = turnRef.current;
      if (!current || current.key !== key) return;
      setTurn(update(current));
    },
    [setTurn],
  );

  // ---- Queries ----

  const conversationsQuery = useApiQuery<unknown>({
    queryKey: [endpointsMetadata.aiAssistant.getConversations, selectedStudio?.id],
    endpoint: endpointsMetadata.aiAssistant.getConversations,
    globalIsLoading: false,
  });

  const conversations = useMemo<IAiConversationListItem[]>(
    () => normalizeConversationList(conversationsQuery.data),
    [conversationsQuery.data],
  );

  const selectConversationParam = useCallback(
    (conversationId: string | null) => {
      setSearchParams(conversationId ? { [CONVERSATION_PARAM]: conversationId } : {}, { replace: true });
    },
    [setSearchParams],
  );

  const conversationQuery = useApiQuery<unknown>(
    {
      queryKey: [endpointsMetadata.aiAssistant.getConversation, selectedStudio?.id, selectedConversationId],
      endpoint: `${endpointsMetadata.aiAssistant.getConversation}/${selectedConversationId}`,
      globalIsLoading: false,
      enabled: !!selectedConversationId,
    },
    {
      retry: (failureCount, error) => !isClientError(error) && failureCount < 2,
      onError: (error) => {
        // A deleted conversation, or one from another studio after a studio switch.
        if (isClientError(error)) {
          getErrorToast(aiAssistantTexts.notFound);
          selectConversationParam(null);
        }
      },
    },
  );

  const conversation = useMemo<IAiConversation | null>(
    () => (selectedConversationId ? normalizeConversation(conversationQuery.data) : null),
    [conversationQuery.data, selectedConversationId],
  );

  const refreshConversations = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [endpointsMetadata.aiAssistant.getConversation] });
    queryClient.invalidateQueries({ queryKey: [endpointsMetadata.aiAssistant.getConversations] });
  }, [queryClient]);

  const refreshStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [endpointsMetadata.aiAssistant.getStatus] });
  }, [queryClient]);

  // A fresh status replaces what a rejected send told us about the budget.
  useEffect(() => {
    setBudgetReachedLocally(false);
  }, [status]);

  // ---- Derived state ----

  const isSending = isActivePhase(turn);
  const isBudgetReached = !!status?.limitReached || budgetReachedLocally;
  const isConversationFull =
    !!conversation?.isFull || (!!selectedConversationId && fullConversationIds.includes(selectedConversationId));
  const banner: AiAssistantBanner = isBudgetReached ? "budget" : isConversationFull ? "conversationFull" : null;

  const entries = useMemo(
    () => buildChatEntries(conversation, turn, !isSending && banner === null),
    [conversation, turn, isSending, banner],
  );

  const isConversationLoading = !!selectedConversationId && conversationQuery.isLoading && !turn;
  const showEmptyState = !isConversationLoading && entries.length === 0;

  const selectedTitle =
    conversation?.title ||
    conversations.find((item) => item.id === selectedConversationId)?.title ||
    (selectedConversationId || turn ? aiAssistantTexts.untitledConversation : "");

  // ---- Helpers ----

  const markConversationFull = useCallback((conversationId: string | null) => {
    if (!conversationId) return;
    setFullConversationIds((ids) => (ids.includes(conversationId) ? ids : [...ids, conversationId]));
  }, []);

  const scrollToBottom = useCallback(() => {
    // The list is column-reverse: its bottom is scrollTop 0.
    const container = messagesContainerRef.current;
    if (container && typeof container.scrollTo === "function") {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const flushText = useCallback(() => {
    if (textTimerRef.current !== null) {
      window.clearTimeout(textTimerRef.current);
      textTimerRef.current = null;
    }
    const pending = pendingTextRef.current;
    pendingTextRef.current = null;
    if (!pending || !pending.text) return;
    updateTurn(pending.key, (current) => ({
      ...current,
      phase: "streaming",
      toolLabel: null,
      answerText: current.answerText + pending.text,
    }));
  }, [updateTurn]);

  const scheduleLateRefresh = useCallback(() => {
    LATE_REFRESH_DELAYS_MS.forEach((delay) => {
      const timer = window.setTimeout(() => {
        refreshConversations();
        refreshStatus();
      }, delay);
      lateRefreshTimersRef.current.push(timer);
    });
  }, [refreshConversations, refreshStatus]);

  // Leaving the conversation the turn belongs to drops the turn (the server keeps answering;
  // the answer is there on return).
  useEffect(() => {
    const current = turnRef.current;
    if (current && current.conversationId !== selectedConversationId) {
      abortRef.current?.abort();
      abortRef.current = null;
      pendingTextRef.current = null;
      setTurn(null);
    }
  }, [selectedConversationId, setTurn]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      lateRefreshTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      if (textTimerRef.current !== null) window.clearTimeout(textTimerRef.current);
    },
    [],
  );

  // ---- Stream ----

  const handleEvent = useCallback(
    (key: string, event: AiStreamEvent) => {
      if (event.type === "text") {
        if (!event.text) return;
        const pending = pendingTextRef.current;
        pendingTextRef.current = {
          key,
          text: pending && pending.key === key ? pending.text + event.text : event.text,
        };
        if (textTimerRef.current === null) {
          textTimerRef.current = window.setTimeout(flushText, TEXT_FLUSH_MS);
        }
        return;
      }

      flushText();

      switch (event.type) {
        case "start": {
          updateTurn(key, (current) => ({
            ...current,
            conversationId: event.conversationId || current.conversationId,
            userMessageId: event.userMessageId ?? current.userMessageId,
            phase: "streaming",
          }));
          const conversationId = turnRef.current?.key === key ? turnRef.current.conversationId : null;
          if (conversationId && conversationId !== selectedConversationId) {
            selectConversationParam(conversationId);
          }
          queryClient.invalidateQueries({ queryKey: [endpointsMetadata.aiAssistant.getConversations] });
          break;
        }
        case "tool":
          updateTurn(key, (current) => ({ ...current, phase: "streaming", toolLabel: event.label || null }));
          break;
        case "supportDraft":
          updateTurn(key, (current) => ({
            ...current,
            supportDraft: event.draft,
            answerMessageId: event.messageId || current.answerMessageId,
          }));
          break;
        case "supportTicket":
          updateTurn(key, (current) => ({
            ...current,
            supportTicket: event.ticket,
            answerMessageId: event.messageId || current.answerMessageId,
          }));
          // "Запитвания към екипа" and its unread dot show the new ticket.
          queryClient.invalidateQueries({ queryKey: [endpointsMetadata.supportChat.getAll] });
          break;
        case "done": {
          updateTurn(key, (current) => ({
            ...current,
            phase: "done",
            toolLabel: null,
            answerMessageId: event.messageId ?? current.answerMessageId,
          }));
          if (event.isFull) markConversationFull(turnRef.current?.conversationId ?? null);
          if (event.limitReached) setBudgetReachedLocally(true);
          refreshConversations();
          refreshStatus();
          break;
        }
        case "error":
          updateTurn(key, (current) => ({
            ...current,
            phase: "error",
            toolLabel: null,
            answerText: "",
            // The draft goes with the failed answer; a created ticket stays (it exists).
            supportDraft: null,
            errorMessage: event.message || aiAssistantTexts.genericError,
          }));
          if (event.code === "budget_exceeded") setBudgetReachedLocally(true);
          if (event.code === "conversation_full") markConversationFull(turnRef.current?.conversationId ?? null);
          refreshConversations();
          refreshStatus();
          break;
        default:
          break;
      }
    },
    [
      flushText,
      markConversationFull,
      queryClient,
      refreshConversations,
      refreshStatus,
      selectConversationParam,
      selectedConversationId,
      updateTurn,
    ],
  );

  const dropTurn = useCallback(
    (key: string) => {
      if (turnRef.current?.key === key) setTurn(null);
    },
    [setTurn],
  );

  /** False when the API refused the message before answering: the composer gets it back. */
  const handleRejection = useCallback(
    (key: string, outcome: Extract<AiSendOutcome, { kind: "rejected" }>): boolean => {
      const { status: httpStatus, message, code } = outcome;
      const conversationId = turnRef.current?.key === key ? turnRef.current.conversationId : null;

      if (httpStatus === 401 || httpStatus === 403) {
        dropTurn(key);
        navigate(routePaths.logout);
        return false;
      }

      if (httpStatus === 451) {
        // Same as the axios interceptor: this domain may not use the API.
        dropTurn(key);
        if (window.location.pathname !== routePaths.invalidDomain) {
          window.location.href = routePaths.invalidDomain;
        }
        return false;
      }

      if (httpStatus === 429) {
        dropTurn(key);
        getErrorToast(aiAssistantTexts.tooManyRequests);
        return false;
      }

      // A conversation that is gone is a 404 too, but with its own code: only a bare 404 means "switched off".
      if (code === "not_found") {
        dropTurn(key);
        getErrorToast(message || aiAssistantTexts.notFound);
        selectConversationParam(null);
        refreshConversations();
        return false;
      }

      if (httpStatus === 404 || code === "disabled") {
        dropTurn(key);
        getErrorToast(aiAssistantTexts.disabled);
        refreshStatus();
        return false;
      }

      if (httpStatus === 400) {
        dropTurn(key);
        switch (code) {
          case "budget_exceeded":
            setBudgetReachedLocally(true);
            refreshStatus();
            break;
          case "conversation_full":
            markConversationFull(conversationId);
            break;
          case "busy":
            getErrorToast(message || aiAssistantTexts.busy);
            break;
          default:
            getErrorToast(message || aiAssistantTexts.genericError);
            break;
        }
        return false;
      }

      // A server failure: keep the question, with an error row and "Опитай отново".
      updateTurn(key, (current) => ({
        ...current,
        phase: "error",
        toolLabel: null,
        answerText: "",
        errorMessage: aiAssistantTexts.genericError,
      }));
      return true;
    },
    [
      dropTurn,
      markConversationFull,
      navigate,
      refreshConversations,
      refreshStatus,
      selectConversationParam,
      updateTurn,
    ],
  );

  const handleOutcome = useCallback(
    (key: string, outcome: AiSendOutcome): boolean => {
      flushText();
      switch (outcome.kind) {
        case "completed":
          return true;
        case "aborted":
        case "interrupted":
          updateTurn(key, (current) =>
            isActivePhase(current)
              ? {
                  ...current,
                  phase: "stopped",
                  toolLabel: null,
                  errorMessage:
                    outcome.kind === "aborted" ? aiAssistantTexts.stopped : aiAssistantTexts.interrupted,
                }
              : current,
          );
          scheduleLateRefresh();
          return true;
        case "network":
          updateTurn(key, (current) => ({
            ...current,
            phase: "error",
            toolLabel: null,
            answerText: "",
            errorMessage: aiAssistantTexts.networkError,
          }));
          return true;
        case "rejected":
          return handleRejection(key, outcome);
        default:
          return true;
      }
    },
    [flushText, handleRejection, scheduleLateRefresh, updateTurn],
  );

  const send = useCallback(
    async (text: string, images: IAiMessageImage[]): Promise<boolean> => {
      if (isActivePhase(turnRef.current) || isBudgetReached || isConversationFull) return false;

      const key = `turn-${Date.now()}`;
      setTurn({
        key,
        conversationId: selectedConversationId,
        userMessageId: null,
        text,
        images,
        answerText: "",
        toolLabel: null,
        supportDraft: null,
        supportTicket: null,
        answerMessageId: null,
        phase: "waiting",
        errorMessage: null,
      });
      scrollToBottom();

      const controller = new AbortController();
      abortRef.current = controller;

      const outcome = await sendAiAssistantMessage({
        request: {
          conversationId: selectedConversationId ?? undefined,
          message: text,
          attachmentIds: images.map((image) => image.id),
          pagePath: location.pathname,
        },
        authToken: currentUser?.authToken,
        signal: controller.signal,
        onEvent: (event) => handleEvent(key, event),
      });

      if (abortRef.current === controller) abortRef.current = null;
      return handleOutcome(key, outcome);
    },
    [
      currentUser?.authToken,
      handleEvent,
      handleOutcome,
      isBudgetReached,
      isConversationFull,
      location.pathname,
      scrollToBottom,
      selectedConversationId,
      setTurn,
    ],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retryLast = useCallback(() => {
    const target = findRetryTarget(conversation, turn);
    if (target) void send(target.text, target.images);
  }, [conversation, send, turn]);

  const selectConversation = useCallback(
    (conversationId: string) => {
      if (conversationId !== selectedConversationId) selectConversationParam(conversationId);
    },
    [selectConversationParam, selectedConversationId],
  );

  const startNewConversation = useCallback(() => {
    if (turnRef.current) {
      abortRef.current?.abort();
      abortRef.current = null;
      setTurn(null);
    }
    selectConversationParam(null);
  }, [selectConversationParam, setTurn]);

  // ---- Delete ----

  const { mutate: deleteMutate, isLoading: isDeleting } = useApiMutation<unknown>({
    onSuccess: () => {
      if (pendingDeleteIdRef.current && pendingDeleteIdRef.current === selectedConversationId) {
        startNewConversation();
      }
      pendingDeleteIdRef.current = null;
    },
    onError: () => {
      pendingDeleteIdRef.current = null;
    },
  });

  const deleteConversation = useCallback(
    (conversationId: string) => {
      pendingDeleteIdRef.current = conversationId;
      deleteMutate({
        endpoint: `${endpointsMetadata.aiAssistant.deleteConversation}/${conversationId}`,
        method: "delete",
        disableSuccessToast: true,
        errorMessage: "Разговорът не беше изтрит. Опитайте отново.",
        invalidateKeys: [[endpointsMetadata.aiAssistant.getConversations]],
      });
    },
    [deleteMutate],
  );

  return {
    status,
    conversations,
    isConversationsLoading: conversationsQuery.isLoading,
    selectedConversationId,
    selectedTitle,
    entries,
    isConversationLoading,
    showEmptyState,
    isSending,
    isDeleting,
    banner,
    resetDateLabel: formatResetDate(status?.resetsOn ?? null),
    messagesContainerRef,
    send,
    stop,
    retryLast,
    selectConversation,
    startNewConversation,
    deleteConversation,
  };
};
